import React, { useEffect, useState } from 'react';
import Select from '@upsales/components/Select/Select';
import { useTranslation } from 'Components/Helpers/translate';
import AccountRelationResource from 'App/resources/AccountRelation';
import logError from 'Helpers/logError';

type Option = {
	id: string;
	title: string;
	icon?: 'plus';
};

const CREATE_OPTION_ID = '__createRelationDescription__';

/** Relation labels already used in this account, most used first. */
export const fetchRelationDescriptions = (): Promise<string[]> =>
	AccountRelationResource.getDescriptions().catch((error: Error) => {
		logError(error, 'Failed to load relation descriptions');
		return [];
	});

export const useRelationDescriptions = (enabled: boolean) => {
	const [descriptions, setDescriptions] = useState<string[]>([]);

	useEffect(() => {
		if (!enabled) {
			return;
		}

		let cancelled = false;

		fetchRelationDescriptions().then(values => {
			if (!cancelled) {
				setDescriptions(values);
			}
		});

		return () => {
			cancelled = true;
		};
	}, [enabled]);

	return descriptions;
};

type Props = {
	value: string;
	onChange: (value: string) => void;
	descriptions: string[];
	placeholder?: string;
	disabled?: boolean;
};

const getAnchor = () => document.querySelector('.Modals');

const matchOption = (option: Option, searchStr: string) =>
	option.id === CREATE_OPTION_ID || option.title.toLowerCase().includes(searchStr.trim().toLowerCase());

const buildOptions = (value: string, descriptions: string[]): Option[] => {
	const options: Option[] = descriptions.map(description => ({ id: description, title: description }));
	const isExisting = descriptions.some(description => description.toLowerCase() === value.toLowerCase());

	if (value && !isExisting) {
		options.unshift({ id: CREATE_OPTION_ID, title: value, icon: 'plus' });
	}

	return options;
};

/** Picks a relation label the account already uses, or keeps whatever the user types. */
const RelationDescriptionSelect = ({ value, onChange, descriptions, placeholder, disabled }: Props) => {
	const { t } = useTranslation();

	return (
		<Select<Option>
			anchor={getAnchor()}
			disabled={disabled}
			matchOptions={matchOption}
			onChange={option => onChange(option?.title ?? '')}
			onClear={() => onChange('')}
			onInput={event => onChange((event.target as HTMLInputElement).value.trim())}
			options={buildOptions(value, descriptions)}
			placeholder={placeholder}
			showSearch
			value={value ? { id: value, title: value } : null}
			virtualized
			language={{ noResults: t('default.noResults'), typeToSearch: t('default.typeToSearch') }}
		/>
	);
};

export default RelationDescriptionSelect;
